import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import NavigationMenu from '../components/NavigationMenu'
import { ServiceProviderType, type ServiceProviderInfo, type ClassificationRequestData, type ClassificationResultData } from '../types'
import { useGetClassificationResultMutation } from '../store/api'
import { setQueryRequest } from '../store/slices'
import { type ActionCreatorWithPayload } from '@reduxjs/toolkit'
import ClipLoader from 'react-spinners/ClipLoader'

enum RequestType {
  File = 'file',
  JSON = 'json',
  Form = 'form',
}

interface RequestTypeInfo {
  type: RequestType
  label: string
}

interface StateProps {
  queryResult: ClassificationResultData | null
}

interface DispatchProps {
  setQueryRequestAction: ActionCreatorWithPayload<ClassificationRequestData, 'classification/setQueryRequest'>
}

type HomePageProps = StateProps & DispatchProps

/**
 * Description
 * Home page component receives the input from user either in JSON text format, JSON file, or text format from the user
 * along with service provider choice.
 * After clicking "Execute button", API call is performed to the Grant Classifier and result is displayed in QerryResultPage.
 * @param {any} {queryResult
 * @param {any} setQueryRequestAction}:HomePageProps
 * @returns {any}
 */
function HomePageComponent ({ queryResult, setQueryRequestAction }: HomePageProps): JSX.Element {
  const navigate = useNavigate()
  const [getClassificationResult] = useGetClassificationResultMutation()
  // Variables for holding input types and text formats

  const [providerTypes, setProviderTypes] = useState<ServiceProviderType[]>([ServiceProviderType.OpenAlex])
  const [requestType, setRequestType] = useState<RequestType>(RequestType.JSON)
  const [title, setTitle] = useState<string>('')
  const [abstract, setAbstract] = useState<string>('')
  const [jsonText, setJsonText] = useState<string>('')
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>(false)

  const providerItems: ServiceProviderInfo[] = [
    {
      label: 'Open Alex',
      type: ServiceProviderType.OpenAlex
    },
    {
      label: 'Open AI',
      type: ServiceProviderType.OpenAI
    }
  ]

  const requestTypeInfoList: RequestTypeInfo[] = [
    {
      label: 'Enter JSON text',
      type: RequestType.JSON
    },
    /*
    {
      label: 'Upload JSON file',
      type: RequestType.File
    },
    */
    {
      label: 'Manually enter text',
      type: RequestType.Form
    }
  ]

  // Set and update provider types if changed by the user.
  const onProviderTypeToggled = useCallback((changedProviderType: ServiceProviderType) => {
    if (providerTypes.includes(changedProviderType)) {
      setProviderTypes(providerTypes.filter((providerType: string) => providerType !== String(changedProviderType)))
    } else {
      setProviderTypes([...providerTypes, changedProviderType])
    }
  }, [providerTypes])

  // This constant hold values from manual text entry input.
  const getClassificationResultFromFormData = useCallback(async () => {
    const request: ClassificationRequestData = {
      services: providerTypes.join(','),
      isWebsiteOriginated: true,
      input: [
        {
          id: '1',
          title,
          abstract
        }
      ]
    }

    // Waits for Grant Classifier API response.
    setQueryRequestAction(request)
    setIsRequestInProgress(true)

    await getClassificationResult({ data: request })
      .then(() => {
        navigate('/result')
      })
      .catch((e) => {
        console.error(e)
      })
  }, [title, abstract, providerTypes, getClassificationResult])

  async function getClassificationResultFromJsonText (): Promise<void> {
    try {
      const request = JSON.parse(jsonText)
      request.isWebsiteOriginated = true
      setQueryRequestAction(request)
      setIsRequestInProgress(true)

      await getClassificationResult({ data: request })
        .then(() => {
          navigate('/result')
        })
        .catch((e) => {
          console.error(e)
        })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <nav id="sidebarMenu" className="col-md-3 col-lg-2 d-md-block bg-light sidebar collapse">
          <NavigationMenu />
        </nav>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
            <h1 className="h2">Lotus Rise: Grant Classifier</h1>
            <div className="btn-toolbar mb-2 mb-md-0">
              {
                /*
                  <div className="btn-group me-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary">Share</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary">Export</button>
                  </div>
                */
              }
            </div>
          </div>
          <div className="row">
            <div className="col-md-7 col-lg-8">
              <div className="row">
                <div className="col-sm-6">
                  <h4 className="mb-3">Service Providers</h4>
                  {
                    providerItems.map((providerItem: ServiceProviderInfo, i: number) => (
                      <div className="form-check" key={i}>
                        <input type="checkbox" className="form-check-input" id={`service-${providerItem.type}`} checked={providerTypes.includes(providerItem.type)} onChange={() => { onProviderTypeToggled(providerItem.type) }}/>
                        <label className="form-check-label" htmlFor={`service-${providerItem.type}`}>Use { providerItem.label } service</label>
                      </div>
                    ))
                  }
                </div>
                <div className="col-sm-6">
                  <h4 className="mb-3">Input Method</h4>
                  <div className="my-3">
                    {
                      requestTypeInfoList.map((info: RequestTypeInfo, i: number) => (
                        <div className="form-check" key={i}>
                          <input id={`method-${info.type}`} type="radio" className="form-check-input" value={info.type} checked={info.type === requestType} onChange={() => { setRequestType(info.type) }}/>
                          <label className="form-check-label" htmlFor={`method-${info.type}`}>{info.label}</label>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              <form className="needs-validation" noValidate>
                <div className="row g-3">
                  {
                    requestType === RequestType.File
                      ? <div className="col-12">
                          <h4 className="mb-3">Upload JSON file</h4>

                          <div className="btn-group me-2">
                            <input type="file" className="form-control btn btn-sm btn-outline-secondary" id="upload-file" aria-describedby="inputGroupFileAddon04" aria-label="Upload" />
                            <button className="btn btn btn-sm btn-outline-secondary" type="button" id="inputGroupFileAddon04">Import</button>
                          </div>
                          <button type="button" className="btn btn-sm btn-outline-primary lr-form-button" onClick={() => { navigate('/result') }}>Execute</button>
                        </div>
                      : null
                  }
                  {
                    requestType === RequestType.JSON
                      ? <div className="col-12">
                          <h4 className="mb-3">Enter JSON text</h4>
                          <textarea className="form-control" rows={10} value={jsonText} onChange={(e) => { setJsonText(e.target.value) }}></textarea>
                          <button type="button" className="btn btn-sm btn-outline-primary lr-form-button" disabled={!jsonText || !jsonText.length || providerTypes.length === 0} onClick={async () => { await getClassificationResultFromJsonText() }}>{
                            isRequestInProgress ? (
                              <ClipLoader
                                color="black"
                                loading={true}
                                size={150}
                                aria-label="Loading Spinner"
                              />
                            ) : 'Execute' }</button>
                        </div>
                      : null
                  }
                  {
                    requestType === RequestType.Form
                      ? <div className="col-12">
                          <h4 className="mb-3">Manually enter text</h4>
                          <label htmlFor="subject" className="form-label">Title</label>
                          <input type="text" className="form-control mb-3" id="subject" value={title} onChange={(e) => { setTitle(e.target.value) }} />
                          <label htmlFor="abstract" className="form-label">Abstract</label>
                          <textarea className="form-control" id="abstract" rows={5} value={abstract} onChange={(e) => { setAbstract(e.target.value) }}></textarea>
                          <button type="button" className="btn btn-sm btn-outline-primary lr-form-button" disabled={!title || !abstract || providerTypes.length === 0} onClick={async () => { await getClassificationResultFromFormData() }}>{
                            isRequestInProgress ? (
                              <ClipLoader
                                color="white"
                                loading={true}
                                size={20}
                                aria-label="Loading Spinner"
                                className="mr-3"
                              />
                            ) : null }Execute</button>
                          <a className="link-primary" href="#" aria-label="Add a new report">
                            <span data-feather="plus-circle"></span>
                          </a>
                        </div>
                      : null
                  }
                  <hr className="my-4" />
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

const mapStateToProps = (state: any): any => {
  return {
    queryResult: state.classification.queryResult
  }
}

const mapDispatchToProps = (dispatch: any): any => ({
  setQueryRequestAction: (payload: any) => dispatch(setQueryRequest(payload))
})

export const HomePage = connect(mapStateToProps, mapDispatchToProps)(HomePageComponent)
